/* ==========================================================================
   TEENMILLIONAIRES · INTERACTIONS
   --------------------------------------------------------------------------
   Vanilla JS, no build step, no dependencies.

   GSAP was considered and deliberately not used: every animation here is a
   scroll reveal, a lerped cursor, or a CSS keyframe. IntersectionObserver +
   requestAnimationFrame cover all of it natively, so shipping a 70kb CDN
   dependency would add a network hop and a point of failure for nothing.
   If a future build needs timeline choreography, drop GSAP in then.

   Modules
     01. Boot / loader
     02. Custom cursor
     03. Nav (scroll state, mobile drawer)
     04. Marquee cloning
     05. Scroll reveals
     06. Page transitions
     07. Email capture
     08. Collection: filters + view toggle
     09. Product: sticky mobile bar
     10. Footer year
     11. Cart quantity
     12. Variant price sync
   ========================================================================== */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer  = window.matchMedia('(pointer: fine)').matches;

  /* 01. BOOT / LOADER ===================================================== */
  /* The loader hides on window load, with a floor of ~900ms so the mark is
     actually readable, and a hard ceiling of 1500ms so a slow asset can never
     hold the page hostage. */

  function initLoader() {
    var loader = document.querySelector('[data-loader]');
    if (!loader) return;

    var MIN = reduceMotion ? 0 : 900;
    var MAX = 1500;
    var start = Date.now();
    var dismissed = false;

    function dismiss() {
      if (dismissed) return;
      dismissed = true;
      loader.classList.add('is-done');
      document.body.classList.add('is-ready');
      // Remove from the a11y tree and the DOM once the fade completes.
      window.setTimeout(function () {
        if (loader.parentNode) loader.parentNode.removeChild(loader);
      }, 700);
    }

    window.addEventListener('load', function () {
      var elapsed = Date.now() - start;
      window.setTimeout(dismiss, Math.max(0, MIN - elapsed));
    });

    window.setTimeout(dismiss, MAX);
  }

  /* 02. CUSTOM CURSOR ===================================================== */
  /* Dot tracks the pointer exactly; the ring lerps behind it for weight.
     Desktop only: a lagging ring on touch is noise, not polish. */

  function initCursor() {
    if (!finePointer || reduceMotion) return;

    var dot  = document.querySelector('[data-cursor]');
    var ring = document.querySelector('[data-cursor-ring]');
    if (!dot || !ring) return;

    var mx = window.innerWidth / 2, my = window.innerHeight / 2;
    var rx = mx, ry = my;

    document.addEventListener('mousemove', function (e) {
      mx = e.clientX;
      my = e.clientY;
      dot.style.transform  = 'translate(' + mx + 'px,' + my + 'px)';
    });

    (function loop() {
      // Lerp toward the pointer. 0.18 reads as weighted without feeling laggy.
      rx += (mx - rx) * 0.18;
      ry += (my - ry) * 0.18;
      ring.style.transform = 'translate(' + rx + 'px,' + ry + 'px)';
      window.requestAnimationFrame(loop);
    }());

    // Bloom the ring over anything interactive.
    var INTERACTIVE = 'a, button, input, select, textarea, [role="button"], .card, .index__row';

    document.addEventListener('mouseover', function (e) {
      if (e.target.closest(INTERACTIVE)) ring.classList.add('is-active');
    });

    document.addEventListener('mouseout', function (e) {
      if (e.target.closest(INTERACTIVE)) ring.classList.remove('is-active');
    });

    // Hide when the pointer leaves the window entirely.
    document.addEventListener('mouseleave', function () {
      dot.classList.add('is-hidden');
      ring.classList.add('is-hidden');
    });

    document.addEventListener('mouseenter', function () {
      dot.classList.remove('is-hidden');
      ring.classList.remove('is-hidden');
    });
  }

  /* 03. NAV =============================================================== */

  function initNav() {
    var nav = document.querySelector('[data-nav]');

    if (nav) {
      var onScroll = function () {
        nav.classList.toggle('is-stuck', window.scrollY > 12);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    var burger = document.querySelector('[data-burger]');
    var drawer = document.querySelector('[data-drawer]');
    if (!burger || !drawer) return;

    function setOpen(open) {
      burger.setAttribute('aria-expanded', String(open));
      drawer.classList.toggle('is-open', open);
      drawer.hidden = !open;
    }

    setOpen(false);

    burger.addEventListener('click', function () {
      setOpen(burger.getAttribute('aria-expanded') !== 'true');
    });

    // Close on navigation or Escape.
    drawer.addEventListener('click', function (e) {
      if (e.target.tagName === 'A') setOpen(false);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') setOpen(false);
    });
  }

  /* 04. MARQUEE =========================================================== */
  /* The CSS animation translates the track by -50%, so the markup needs
     exactly two identical groups for the loop to be seamless. The second
     group is cloned here and hidden from assistive tech. */

  function initMarquees() {
    document.querySelectorAll('[data-marquee]').forEach(function (track) {
      var group = track.firstElementChild;
      if (!group) return;

      var clone = group.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    });
  }

  /* 05. SCROLL REVEALS ==================================================== */

  function initReveals() {
    var targets = document.querySelectorAll('[data-reveal]');
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    targets.forEach(function (el, i) {
      // Stagger siblings only: a long page shouldn't accumulate delay.
      var stagger = parseInt(el.getAttribute('data-reveal'), 10);
      if (!isNaN(stagger)) el.style.setProperty('--delay', stagger + 'ms');
      io.observe(el);
    });
  }

  /* 06. PAGE TRANSITIONS ================================================== */
  /* Fade the viewport out before an internal navigation commits, and fade
     back in on arrival (including on bfcache restore). */

  function initTransitions() {
    var veil = document.querySelector('[data-veil]');
    if (!veil || reduceMotion) return;

    document.addEventListener('click', function (e) {
      var link = e.target.closest('a');
      if (!link) return;

      var href = link.getAttribute('href');
      if (!href) return;

      // Skip anchors, new tabs, downloads, and anything off-origin.
      if (href.charAt(0) === '#') return;
      if (link.target && link.target !== '_self') return;
      if (link.hasAttribute('download')) return;
      if (/^(mailto:|tel:|https?:\/\/)/i.test(href) && link.host !== window.location.host) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

      e.preventDefault();
      veil.classList.add('is-on');
      window.setTimeout(function () { window.location.href = href; }, 300);
    });

    // Clear the veil when returning via the back/forward cache.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) veil.classList.remove('is-on');
    });
  }

  /* 07. EMAIL CAPTURE ===================================================== */
  /* Handled natively by Shopify. The capture section renders a
     {% form 'customer' %}, which posts to Shopify and adds the subscriber to
     Customers tagged "newsletter". No JS interception: hijacking the submit
     would break that POST and silently drop signups. */

  /* 08. COLLECTION: FILTERS + VIEW TOGGLE ================================= */

  function initCollection() {
    var results = document.querySelector('[data-results]');
    if (!results) return;

    var filters  = Array.prototype.slice.call(document.querySelectorAll('[data-filter]'));
    var countEl  = document.querySelector('[data-count]');
    var emptyEl  = document.querySelector('[data-empty]');
    // Both views hold the same SKUs, so filtering targets every item at once.
    var items    = Array.prototype.slice.call(results.querySelectorAll('[data-tier]'));

    function apply(tier) {
      var shown = 0;

      items.forEach(function (el) {
        var match = tier === 'all' || el.getAttribute('data-tier') === tier;
        el.classList.toggle('is-filtered-out', !match);
        // Count the grid only: items are duplicated across the two views.
        if (match && el.closest('.view-grid')) shown++;
      });

      if (countEl) {
        countEl.textContent = String(shown).padStart(2, '0') + ' / ' +
          String(items.filter(function (el) { return el.closest('.view-grid'); }).length).padStart(2, '0');
      }

      if (emptyEl) emptyEl.hidden = shown > 0;
    }

    filters.forEach(function (btn) {
      btn.addEventListener('click', function () {
        filters.forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
        btn.setAttribute('aria-pressed', 'true');
        apply(btn.getAttribute('data-filter'));
      });
    });

    // --- View toggle (card grid <-> dense index) ---
    var views = Array.prototype.slice.call(document.querySelectorAll('[data-view-btn]'));

    function setView(view) {
      results.setAttribute('data-view', view);
      views.forEach(function (b) {
        b.setAttribute('aria-pressed', String(b.getAttribute('data-view-btn') === view));
      });
      try { window.localStorage.setItem('tm:view', view); } catch (err) { /* private mode */ }
    }

    views.forEach(function (btn) {
      btn.addEventListener('click', function () {
        setView(btn.getAttribute('data-view-btn'));
      });
    });

    // Restore the last-used view.
    var saved = null;
    try { saved = window.localStorage.getItem('tm:view'); } catch (err) { /* noop */ }
    setView(saved === 'index' ? 'index' : 'grid');

    apply('all');
  }

  /* 09. PRODUCT: STICKY MOBILE BAR ======================================== */
  /* Rises once the main buy panel has scrolled out of view, so the CTA is
     never duplicated on screen. */

  function initStickyBar() {
    var bar = document.querySelector('[data-stickybar]');
    // Observe the buy panel itself, not a marker element. An empty inline
    // span has zero area and never reports as intersecting, which would
    // leave the bar stuck up from the moment the page loads.
    var anchor = document.querySelector('.buy') || document.querySelector('[data-buy-anchor]');
    if (!bar || !anchor) return;

    document.body.classList.add('has-stickybar');

    if (!('IntersectionObserver' in window)) {
      bar.classList.add('is-up');
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.classList.toggle('is-up', !entry.isIntersecting);
      });
    }, { threshold: 0 });

    io.observe(anchor);
  }

  /* 10. FOOTER YEAR ======================================================= */

  function initYear() {
    document.querySelectorAll('[data-year]').forEach(function (el) {
      el.textContent = String(new Date().getFullYear());
    });
  }

  /* 11. CART QUANTITY ===================================================== */
  /* Uses Shopify's /cart/change.js AJAX endpoint. Sending quantity 0 removes
     the line, which is how the minus button on a single item deletes it. */

  function initCart() {
    var buttons = document.querySelectorAll('[data-qty]');
    if (!buttons.length) return;

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var line = parseInt(btn.getAttribute('data-qty'), 10);
        var delta = parseInt(btn.getAttribute('data-delta'), 10);
        var out = document.getElementById('qty-' + line);
        if (!out) return;

        var next = Math.max(0, parseInt(out.textContent.trim(), 10) + delta);

        // Lock the row's buttons so a double-click can't queue two changes.
        buttons.forEach(function (b) { b.disabled = true; });

        fetch(window.Shopify && window.Shopify.routes
                ? window.Shopify.routes.root + 'cart/change.js'
                : '/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ line: line, quantity: next })
        })
        .then(function (r) {
          if (!r.ok) throw new Error('Cart update failed: ' + r.status);
          return r.json();
        })
        .then(function () { window.location.reload(); })
        .catch(function (err) {
          // Re-enable so the customer can retry rather than facing a dead row.
          buttons.forEach(function (b) { b.disabled = false; });
          console.error(err);
        });
      });
    });
  }

  /* 12. VARIANT PRICE SYNC ================================================ */
  /* Without this the panel keeps showing the first variant's price after the
     customer picks a different one, so the figure on screen disagrees with
     what checkout will charge. */

  function initVariants() {
    var select = document.querySelector('[data-variant-select]');
    if (!select) return;

    var targets = [
      document.querySelector('.buy__now'),
      document.querySelector('.stickybar__now')
    ].filter(Boolean);

    if (!targets.length) return;

    select.addEventListener('change', function () {
      var opt = select.options[select.selectedIndex];
      var price = opt && opt.getAttribute('data-price');
      if (!price) return;

      targets.forEach(function (el) { el.textContent = price; });

      // A compare-at price belongs to the default variant only; once the
      // customer switches, showing it would misstate the discount.
      var was = document.querySelector('.buy__was');
      var save = document.querySelector('.buy__save');
      if (was) was.hidden = true;
      if (save) save.hidden = true;
    });
  }

  /* BOOT ================================================================== */

  function boot() {
    initLoader();
    initCursor();
    initNav();
    initMarquees();
    initReveals();
    initTransitions();
    initCollection();
    initStickyBar();
    initCart();
    initVariants();
    initYear();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}());
